"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { GruvWordmark } from "@project-x/ui-kit";
import { WithdrawalButton } from "@/components/legal/WithdrawalButton";

interface FooterLink {
  href: string;
  labelKey: string;
}

/** Published GRUV consumer apps. */
const CONSUMER_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.gruv.app";
const CONSUMER_APP_STORE_URL =
  "https://apps.apple.com/ar/app/gruv/id6786763030";

/**
 * ARCA Formulario 960 public fiscal data for gruv.fm.
 * The QR URL is the published AFIP snippet (it redirects to HTTPS).
 * The badge image is loaded over HTTPS so the site does not block it as mixed content.
 */
const ARCA_FISCAL_QR_URL =
  "http://qr.afip.gob.ar/?qr=AEX3K6ZdNIbG1vP3TdKiDw,,";
const ARCA_FISCAL_BADGE_URL =
  "https://www.afip.gob.ar/images/f960/DATAWEB.jpg";

/** Main footer links in the public brand-navigation order. */
const FOOTER_LINKS: FooterLink[] = [
  { href: "/about", labelKey: "footer.links.about" },
  { href: "/what-is", labelKey: "footer.links.whatIs" },
  { href: "/faq", labelKey: "footer.links.faq" },
  { href: "/contact", labelKey: "footer.links.contact" },
  { href: "https://manager.gruv.fm", labelKey: "footer.links.managerLogin" },
];

export function GruvFooter() {
  const t = useTranslations("layout");

  return (
    <footer className="gruv-footer">
      <div className="gruv-footer__inner">
        {/* Top Row: Logo | Stores (Left) --- Links (Right) */}
        <div className="gruv-footer__top">
          <div className="gruv-footer__brand" style={{ flexDirection: 'row', alignItems: 'center' }}>
            <GruvWordmark className="gruv-footer__logo" height={22} />

            <div className="gruv-footer__divider"></div>

            <div className="gruv-footer__store-links">
              <a
                href={CONSUMER_PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="gruv-footer__store-button"
              >
                {t("footer.store.googlePlay")}
              </a>
              <a
                href={CONSUMER_APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="gruv-footer__store-button"
              >
                {t("footer.store.appStore")}
              </a>
            </div>
          </div>

          <div className="gruv-footer__links">
            {FOOTER_LINKS.map((link) =>
              link.href.startsWith("/") ? (
                <Link key={link.labelKey} href={link.href} className="gruv-footer__link">
                  {t(link.labelKey)}
                </Link>
              ) : (
                <a key={link.labelKey} href={link.href} className="gruv-footer__link">
                  {t(link.labelKey)}
                </a>
              ),
            )}
          </div>
        </div>

        {/* Horizontal Divider */}
        <div className="gruv-footer__horizontal-divider"></div>

        {/* Bottom Row: Fiscal badge + copyright (Left) --- Legal (Right) */}
        <div className="gruv-footer__bottom">
          <div className="gruv-footer__meta">
            <a
              href={ARCA_FISCAL_QR_URL}
              target="_F960AFIPInfo"
              rel="noopener"
              className="gruv-footer__fiscal"
            >
              {/* Official Form 960 badge must be requested from afip.gob.ar, not the image optimizer. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={ARCA_FISCAL_BADGE_URL}
                alt="Data Fiscal ARCA"
                width={239}
                height={327}
                className="gruv-footer__fiscal-badge"
              />
            </a>
            <p className="gruv-footer__copyright">© {new Date().getFullYear()} GRUV. {t("footer.legal.rights")}</p>
          </div>

          <div className="gruv-footer__legal-links">
            <WithdrawalButton className="gruv-footer__link gruv-footer__withdrawal" />
            <Link href="/terms" className="gruv-footer__link">
              {t("footer.legal.terms")}
            </Link>
            <Link href="/privacy" className="gruv-footer__link">
              {t("footer.legal.privacy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
